import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function Field({ label, children, required }) {
  return (
    <div className="space-y-1.5">
      <Label>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      {children}
    </div>
  );
}

export default function NewCustomer() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState({ countries: ["USA", "Canada"] });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    contact_number: "",
    primary_issue: "",
    call_converted: null, // null until chosen
    not_converted_reason: "",
    email: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
    sale_amount: "",
    plan_purchased: "",
    ticket_description: "",
  });

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const converted = form.call_converted === true;
  const notConverted = form.call_converted === false;

  const submit = async (e) => {
    e.preventDefault();
    if (form.call_converted === null) {
      toast.error("Please indicate if the call was converted.");
      return;
    }
    setSaving(true);
    const payload = {
      first_name: form.first_name,
      middle_name: form.middle_name,
      last_name: form.last_name,
      contact_number: form.contact_number,
      primary_issue: form.primary_issue,
      call_converted: form.call_converted,
    };
    if (notConverted) payload.not_converted_reason = form.not_converted_reason;
    if (converted) {
      Object.assign(payload, {
        email: form.email,
        address1: form.address1,
        address2: form.address2,
        city: form.city,
        state: form.state,
        zip_code: form.zip_code,
        country: form.country || null,
        sale_amount: form.sale_amount ? parseFloat(form.sale_amount) : null,
        plan_purchased: form.plan_purchased,
        ticket_description: form.ticket_description,
      });
    }
    try {
      const { data } = await api.post("/customers", payload);
      if (data.auto_ticket) {
        toast.success(
          `Customer ${data.customer_id} created. Ticket ${data.auto_ticket.ticket_id} assigned to ${data.auto_ticket.assigned_to_name}. Welcome email sent.`
        );
      } else {
        toast.success(`Lead ${data.customer_id} recorded (not converted).`);
      }
      navigate(`/customers/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="text-4xl mb-1">New Customer</h1>
      <p className="text-muted-foreground mb-8">
        A unique ID is generated automatically on save.
      </p>

      <form onSubmit={submit} className="space-y-8">
        {/* Base details */}
        <section className="border border-border bg-card p-6 space-y-5">
          <h2 className="text-lg border-b border-border pb-2">Call Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="First Name" required>
              <Input data-testid="input-first-name" className="rounded-none" value={form.first_name} onChange={set("first_name")} required />
            </Field>
            <Field label="Middle Name">
              <Input data-testid="input-middle-name" className="rounded-none" value={form.middle_name} onChange={set("middle_name")} />
            </Field>
            <Field label="Last Name" required>
              <Input data-testid="input-last-name" className="rounded-none" value={form.last_name} onChange={set("last_name")} required />
            </Field>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Contact Number" required>
              <Input data-testid="input-contact" className="rounded-none" value={form.contact_number} onChange={set("contact_number")} required />
            </Field>
            <Field label="Primary Issue" required>
              <Input data-testid="input-primary-issue" className="rounded-none" value={form.primary_issue} onChange={set("primary_issue")} required />
            </Field>
          </div>

          <Field label="Call Converted?" required>
            <RadioGroup
              className="flex gap-6 pt-1"
              value={form.call_converted === null ? "" : form.call_converted ? "yes" : "no"}
              onValueChange={(v) => set("call_converted")(v === "yes")}
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="yes" data-testid="converted-yes" />
                <span>Yes</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <RadioGroupItem value="no" data-testid="converted-no" />
                <span>No</span>
              </label>
            </RadioGroup>
          </Field>
        </section>

        {/* Not converted */}
        {notConverted && (
          <section data-testid="not-converted-section" className="border border-border bg-card p-6 space-y-4">
            <h2 className="text-lg border-b border-border pb-2">Reason Not Converted</h2>
            <Field label="Why did the call not convert to a sale?" required>
              <Textarea
                data-testid="input-not-converted-reason"
                className="rounded-none min-h-24"
                value={form.not_converted_reason}
                onChange={set("not_converted_reason")}
                required
              />
            </Field>
          </section>
        )}

        {/* Converted expansion */}
        {converted && (
          <section data-testid="converted-section" className="border border-border bg-card p-6 space-y-5">
            <h2 className="text-lg border-b border-border pb-2">Sale & Shipping Details</h2>
            <Field label="Email of the Customer" required>
              <Input type="email" data-testid="input-email" className="rounded-none" value={form.email} onChange={set("email")} required={converted} />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Address 1">
                <Input data-testid="input-address1" className="rounded-none" value={form.address1} onChange={set("address1")} />
              </Field>
              <Field label="Address 2">
                <Input data-testid="input-address2" className="rounded-none" value={form.address2} onChange={set("address2")} />
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Field label="City">
                <Input data-testid="input-city" className="rounded-none" value={form.city} onChange={set("city")} />
              </Field>
              <Field label="State">
                <Input data-testid="input-state" className="rounded-none" value={form.state} onChange={set("state")} />
              </Field>
              <Field label="Zip Code">
                <Input data-testid="input-zip" className="rounded-none" value={form.zip_code} onChange={set("zip_code")} />
              </Field>
              <Field label="Country">
                <Select value={form.country} onValueChange={set("country")}>
                  <SelectTrigger data-testid="select-country" className="rounded-none">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {meta.countries.map((c) => (
                      <SelectItem key={c} value={c} data-testid={`country-${c}`}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Sale Amount ($)">
                <Input type="number" step="0.01" data-testid="input-sale-amount" className="rounded-none" value={form.sale_amount} onChange={set("sale_amount")} />
              </Field>
              <Field label="Plan Purchased">
                <Input data-testid="input-plan" className="rounded-none" value={form.plan_purchased} onChange={set("plan_purchased")} />
              </Field>
            </div>
            <Field label="Description to Generate a Ticket">
              <Textarea
                data-testid="input-ticket-description"
                className="rounded-none min-h-24"
                placeholder="Describe the issue for the technician…"
                value={form.ticket_description}
                onChange={set("ticket_description")}
              />
            </Field>
          </section>
        )}

        <div className="flex gap-3">
          <Button type="submit" data-testid="submit-customer-btn" disabled={saving} className="rounded-none">
            {saving ? "Saving…" : "Save Customer"}
          </Button>
          <Button type="button" variant="outline" className="rounded-none" onClick={() => navigate("/customers")}>
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
}
